import React, { useState, useEffect } from "react";
import api, { formatTL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plate } from "@/components/Plate";
import { toast } from "sonner";
import { Money, CreditCard, Bank, Percent, CaretDown, CaretUp, TicketIcon, CheckCircle, Storefront } from "@phosphor-icons/react";
import { BusinessQuickTag, canTagSession } from "@/components/BusinessTag";
import { useAuth } from "@/context/AuthContext";

const METHODS = [
  { key: "cash", label: "Nakit", icon: Money },
  { key: "card", label: "Kart", icon: CreditCard },
  { key: "bank_transfer", label: "EFT / Havale", icon: Bank },
];

const PRESET_PERCENTS = [10, 20, 30, 50];

/**
 * Unified pay + optional discount dialog.
 * Discount applied first (via /discounts/session), then pay (via /sessions/:id/pay).
 */
export default function PayMethodDialog({ session, open, onOpenChange, onDone, hideCoupon = false }) {
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [exemptBusy, setExemptBusy] = useState(false);

  // Discount section
  const [showDiscount, setShowDiscount] = useState(false);
  const [mode, setMode] = useState("auto_from_paid");
  const [paid, setPaid] = useState("");
  const [percent, setPercent] = useState("10");
  const [amount, setAmount] = useState("0");
  const [reason, setReason] = useState("");

  // Coupon section
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // { discount, new_fee }
  const [couponBusy, setCouponBusy] = useState(false);
  const [sess, setSess] = useState(session);
  const { user } = useAuth();

  useEffect(() => {
    setSess(session);
    if (open && session) {
      setMethod("cash"); setNote(""); setShowDiscount(false);
      setMode("auto_from_paid");
      setPaid(String(session.fee || 0));
      setPercent("10"); setAmount("0"); setReason("");
      setCouponCode(""); setCouponApplied(null);
    }
  }, [open, session]);

  if (!session || !sess) return null;

  const onBusinessUpdated = (updated) => {
    setSess(updated);
    setPaid(String(updated.fee || 0));
    setCouponApplied(null);
    if (updated.payment_status === "muaf") {
      toast.success(`${updated.plate} işletme anlaşmasıyla muaf — ödeme gerekmez`);
      onOpenChange(false);
      onDone?.();
    }
  };

  const businessBlock = canTagSession(user, sess) || sess.business_account_id ? (
    <div className="rounded-sm border-2 border-violet-300 bg-violet-50/60 p-3 space-y-2" data-testid="pay-business-block">
      <div className="text-xs font-semibold uppercase tracking-widest text-violet-800 flex items-center gap-2">
        <Storefront size={14} weight="fill" /> İşletme Müşterisi mi?
      </div>
      <BusinessQuickTag session={sess} onUpdated={onBusinessUpdated} />
      {sess.business_account_id && sess.fee_note && (
        <div className="text-[11px] text-violet-800" data-testid="pay-business-note">{sess.fee_note}</div>
      )}
    </div>
  ) : null;

  // Abonelik / muaf oturumlar için ödeme akışı devre dışı
  const isSubscriberFree = !!sess.subscription_used || sess.payment_status === "muaf"
                            || !!sess.is_subscriber;
  // Sadece hâlâ `bekliyor` olan (Unpaid listesindeki) session'ları muaf'a
  // çevirebiliriz — zaten muaf olanda buton anlamsız.
  const canMarkExempt = isSubscriberFree && sess.payment_status === "bekliyor";

  const markSubscribedExempt = async () => {
    setExemptBusy(true);
    try {
      await api.post(`/sessions/${sess.id}/mark-subscribed-exempt`);
      toast.success("Oturum aboneliğe göre muaf işaretlendi");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Muaf işaretlenemedi");
    } finally { setExemptBusy(false); }
  };

  if (isSubscriberFree) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plate country={sess.country_code}>{sess.plate}</Plate>
              <span>· Abonelik / Muaf</span>
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 rounded-sm border-2 border-green-400 bg-green-50 text-center space-y-3">
            <CheckCircle size={48} weight="fill" className="text-green-600 mx-auto" />
            <div>
              <div className="text-xs uppercase tracking-widest text-green-700 font-semibold">
                Bu plaka aktif abonelik kapsamında
              </div>
              <div className="text-2xl font-bold font-mono text-green-800 mt-1">
                Ödeme gerekmez
              </div>
            </div>
            <div className="text-xs text-green-700/80 leading-relaxed">
              Aylık aboneliği aktif olan araçlar otopark çıkışında ücret ödemez.
              Ödeme almak için önce mevcut aboneliği iptal edin veya süresi dolmasını bekleyin.
            </div>
          </div>
          {canMarkExempt ? (
            <div className="space-y-2">
              <Button
                onClick={markSubscribedExempt}
                disabled={exemptBusy}
                data-testid="pay-mark-exempt-btn"
                className="w-full rounded-sm bg-green-600 hover:bg-green-700 text-white uppercase tracking-widest font-semibold h-11"
              >
                <CheckCircle size={16} weight="fill" />
                {exemptBusy ? "İşleniyor..." : "Aboneliğe Göre Muaf İşaretle ve Kapat"}
              </Button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="pay-close-subscriber-btn"
                className="w-full text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 tracking-widest uppercase py-2"
              >
                Sadece Kapat (Ödenmemişte Bırak)
              </button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}
                    data-testid="pay-close-subscriber-btn"
                    className="w-full rounded-sm uppercase tracking-widest font-semibold">
              Tamam
            </Button>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  const feeAfterCoupon = couponApplied ? couponApplied.new_fee : (sess.fee || 0);
  const origFee = feeAfterCoupon;
  let discountAmount = 0;
  let newFee = origFee;
  if (showDiscount) {
    if (mode === "percent") {
      const p = Math.max(0, Math.min(100, Number(percent) || 0));
      discountAmount = Number((origFee * p / 100).toFixed(2));
      newFee = Number((origFee - discountAmount).toFixed(2));
    } else if (mode === "amount") {
      const a = Math.max(0, Number(amount) || 0);
      discountAmount = Math.min(origFee, a);
      newFee = Number((origFee - discountAmount).toFixed(2));
    } else if (mode === "auto_from_paid") {
      const p = Math.max(0, Number(paid) || 0);
      newFee = Math.min(origFee, p);
      discountAmount = Number((origFee - newFee).toFixed(2));
    }
  }
  const discountPercent = origFee > 0
    ? Number(((discountAmount / origFee) * 100).toFixed(2))
    : 0;

  const submit = async () => {
    setBusy(true);
    try {
      // Step 0: apply coupon (backend already updated session fee)
      // Step 1: Apply discount if any (on top of coupon-reduced fee)
      if (showDiscount && discountAmount > 0) {
        const body = { session_id: sess.id, kind: mode, reason };
        if (mode === "percent") body.value = Number(percent);
        else if (mode === "amount") body.value = Number(amount);
        else if (mode === "auto_from_paid") body.paid_amount = Number(paid);
        await api.post("/discounts/session", body);
      }
      // Step 2: Mark paid
      await api.post(`/sessions/${sess.id}/pay`, { method, note });
      const methodLabel = METHODS.find(m => m.key === method).label;
      const parts = [];
      if (couponApplied) parts.push(`Kupon: -${formatTL(couponApplied.discount)}`);
      if (discountAmount > 0) parts.push(`İndirim: -${formatTL(discountAmount)}`);
      if (parts.length) {
        toast.success(`${formatTL(newFee)} ${methodLabel} ile alındı (${parts.join(", ")})`);
      } else {
        toast.success(`Ödendi olarak işaretlendi (${methodLabel})`);
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    } finally { setBusy(false); }
  };

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return toast.error("Kupon kodu girin");
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/apply", {
        code, session_id: sess.id,
      });
      setCouponApplied({ discount: data.discount, new_fee: data.new_fee, code });
      // Since coupon mutates session, update paid preset for auto_from_paid mode
      setPaid(String(data.new_fee));
      toast.success(`Kupon uygulandı: -${formatTL(data.discount)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kupon geçersiz");
    } finally { setCouponBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Ödeme Al <Plate country={sess.country_code}>{sess.plate}</Plate>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {businessBlock}
          <div className="p-4 bg-muted rounded-sm text-center">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              {discountAmount > 0 ? "Tahsil Edilecek" : "Tutar"}
            </div>
            {discountAmount > 0 && (
              <div className="text-xs text-muted-foreground line-through font-mono">{formatTL(origFee)}</div>
            )}
            <div className="text-3xl font-bold font-mono text-primary mt-1" data-testid="pay-amount-display">
              {formatTL(newFee)}
            </div>
            {sess.business_account_id && sess.pre_business_fee != null && sess.pre_business_fee !== sess.fee && (
              <div className="text-xs text-violet-700 font-semibold" data-testid="pay-business-deduction">
                Normal: {formatTL(sess.pre_business_fee)} · {sess.business_name} payı düşüldü
              </div>
            )}
            {discountAmount > 0 && (
              <div className="text-xs text-amber-600 font-semibold" data-testid="pay-discount-badge">
                İndirim: -{formatTL(discountAmount)} ({discountPercent}%)
              </div>
            )}
          </div>

          {/* Coupon code input */}
          {!hideCoupon && (!couponApplied ? (
            <div>
              <Label className="text-xs">Kupon Kodu (opsiyonel)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="rounded-sm font-mono uppercase h-9"
                  placeholder="INDIRIM10"
                  data-testid="pay-coupon-code"
                />
                <Button
                  variant="outline"
                  onClick={applyCoupon}
                  disabled={couponBusy || !couponCode}
                  data-testid="pay-coupon-apply-btn"
                  className="rounded-sm uppercase tracking-widest text-xs h-9"
                >
                  <TicketIcon size={14} /> Uygula
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-2 border border-green-400 bg-green-50 rounded-sm">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-green-600" />
                <div>
                  <span className="text-xs uppercase tracking-widest font-semibold">Kupon:</span>{" "}
                  <span className="font-mono font-bold" data-testid="pay-coupon-applied-code">{couponApplied.code}</span>
                </div>
              </div>
              <span className="font-mono font-bold text-green-700" data-testid="pay-coupon-discount">
                -{formatTL(couponApplied.discount)}
              </span>
            </div>
          ))}

          {/* Discount toggle */}
          <button
            type="button"
            onClick={() => setShowDiscount(v => !v)}
            data-testid="pay-toggle-discount"
            className="w-full flex items-center justify-between p-3 rounded-sm border border-border hover:border-primary transition text-sm"
          >
            <span className="flex items-center gap-2">
              <Percent size={14} className="text-amber-600" />
              <span className="uppercase tracking-widest font-semibold">İndirim Uygula</span>
              {showDiscount && discountAmount > 0 && (
                <span className="text-amber-600 font-mono">
                  -{formatTL(discountAmount)}
                </span>
              )}
            </span>
            {showDiscount ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </button>

          {showDiscount && (
            <div className="p-3 border border-amber-200 bg-amber-50/50 rounded-sm space-y-3">
              <div>
                <Label className="text-xs">İndirim Tipi</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="discount-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto_from_paid">Alınan tutardan otomatik hesapla</SelectItem>
                    <SelectItem value="percent">Yüzde (%)</SelectItem>
                    <SelectItem value="amount">Sabit tutar (₺)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {mode === "auto_from_paid" && (
                <div>
                  <Label className="text-xs">Alınan Tutar (₺)</Label>
                  <Input type="number" step="0.01" value={paid}
                         onChange={(e) => setPaid(e.target.value)}
                         className="rounded-sm font-mono h-9"
                         data-testid="discount-paid-input" />
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Ör: {formatTL(origFee)} yerine {formatTL(Number(paid) || 0)} alındıysa fark otomatik iskonto olur
                  </div>
                </div>
              )}

              {mode === "percent" && (
                <div>
                  <Label className="text-xs">Yüzde (%)</Label>
                  <div className="flex gap-1 mb-2">
                    {PRESET_PERCENTS.map((p) => (
                      <Button key={p} size="sm" variant={percent === String(p) ? "default" : "outline"}
                              className="rounded-sm flex-1 h-7 text-xs" onClick={() => setPercent(String(p))}>
                        %{p}
                      </Button>
                    ))}
                  </div>
                  <Input type="number" min="0" max="100" value={percent}
                         onChange={(e) => setPercent(e.target.value)}
                         className="rounded-sm font-mono h-9" data-testid="discount-percent-input" />
                </div>
              )}

              {mode === "amount" && (
                <div>
                  <Label className="text-xs">İndirim Tutarı (₺)</Label>
                  <Input type="number" step="0.01" value={amount}
                         onChange={(e) => setAmount(e.target.value)}
                         className="rounded-sm font-mono h-9" data-testid="discount-amount-input" />
                </div>
              )}

              <div>
                <Label className="text-xs">Sebep (opsiyonel)</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                          className="rounded-sm text-sm" rows={2}
                          placeholder="Müdür onayı / müşteri şikayeti..." />
              </div>
            </div>
          )}

          <div>
            <Label>Ödeme Yöntemi</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {METHODS.map((m) => {
                const Icon = m.icon;
                const active = method === m.key;
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMethod(m.key)}
                    data-testid={`pay-method-${m.key}`}
                    className={`p-3 rounded-sm border-2 flex flex-col items-center gap-1 transition ${
                      active ? "border-primary bg-primary/10" : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <Icon size={24} weight={active ? "fill" : "regular"}
                          className={active ? "text-primary" : "text-muted-foreground"} />
                    <span className={`text-[11px] uppercase tracking-widest font-semibold ${active ? "text-primary" : ""}`}>
                      {m.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Not (opsiyonel)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   className="rounded-sm h-9" placeholder="Referans no / açıklama..." />
          </div>

          <Button
            data-testid="pay-confirm-btn"
            onClick={submit}
            disabled={busy || newFee < 0}
            className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold h-11"
          >
            {busy ? "İşleniyor..." : (
              discountAmount > 0
                ? `${formatTL(newFee)} tahsil et`
                : "Ödendi olarak İşaretle"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
